import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  BASLIK: "",
  IKON_ADI: "",
  RENK: "#4CAF50",
  SIRA: "",
  IS_BLOG: false,
  AKTIF: true,
};

const CiftciBilgiMenuleri = () => {
  document.title = "Çiftçi Bilgi Menüleri | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [menuler, setMenuler] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchMenuler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/CIFTCI_BILGI_MENULERI?$orderby=SIRA asc`);
      const result = await res.json();
      setMenuler(result.value || []);
    } catch {
      setError("Menüler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchMenuler(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (menu) => {
    setFormData({
      ID: menu.ID,
      BASLIK: menu.BASLIK || "",
      IKON_ADI: menu.IKON_ADI || "",
      RENK: menu.RENK || "#4CAF50",
      SIRA: menu.SIRA ?? "",
      IS_BLOG: !!menu.IS_BLOG,
      AKTIF: menu.AKTIF !== false,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.BASLIK.trim()) {
      setError("Başlık zorunludur.");
      return;
    }
    if (!formData.IKON_ADI.trim()) {
      setError("İkon adı zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        BASLIK: formData.BASLIK,
        IKON_ADI: formData.IKON_ADI,
        RENK: formData.RENK,
        SIRA: Number(formData.SIRA) || 0,
        IS_BLOG: formData.IS_BLOG,
        AKTIF: formData.AKTIF,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/CIFTCI_BILGI_MENULERI(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Menü başarıyla güncellendi.");
      } else {
        await fetch(`${BASE_URL}/CIFTCI_BILGI_MENULERI`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Menü başarıyla eklendi.");
      }

      setModal(false);
      fetchMenuler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu menüyü silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/CIFTCI_BILGI_MENULERI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Menü silindi.");
      fetchMenuler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "Sıra",
      accessorKey: "SIRA",
      enableSorting: true,
      cell: (c) => <Badge color="secondary" pill className="px-3">{c.getValue()}</Badge>,
    },
    {
      header: "Başlık",
      accessorKey: "BASLIK",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue()}</span>,
    },
    {
      header: "İkon",
      accessorKey: "IKON_ADI",
      enableSorting: false,
      cell: (c) => (
        <span className="d-flex align-items-center gap-2">
          <i className={`mdi mdi-${c.getValue()} font-size-20`} />
          <code className="small">{c.getValue()}</code>
        </span>
      ),
    },
    {
      header: "Renk",
      accessorKey: "RENK",
      enableSorting: false,
      cell: (c) => (
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 24, height: 24, borderRadius: 4, backgroundColor: c.getValue(), border: "1px solid #dee2e6" }} />
          <code className="small">{c.getValue()}</code>
        </div>
      ),
    },
    {
      header: "Blog mu?",
      accessorKey: "IS_BLOG",
      enableSorting: false,
      cell: (c) => c.getValue()
        ? <Badge color="primary" pill>Blog</Badge>
        : <Badge color="light" className="text-muted">Hayır</Badge>,
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      cell: (c) => (
        <Badge color={c.getValue() ? "success" : "danger"}>
          {c.getValue() ? "Aktif" : "Pasif"}
        </Badge>
      ),
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const m = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(m)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(m.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Çiftçi Bilgi Menüleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Çiftçi Bilgi Menüleri</h4>
                      <p className="text-muted small mb-0">
                        Mobil uygulamada gösterilen bilgi menüsü kategorilerini buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Menü
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={menuler}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Menü ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-pencil" : "mdi-plus-circle"} me-2 text-primary`}></i>
          {isEditing ? `Menü Düzenle (#${formData.ID})` : "Yeni Menü Ekle"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-bold">Başlık <span className="text-danger">*</span></Label>
            <Input
              value={formData.BASLIK}
              onChange={(e) => f("BASLIK", e.target.value)}
              placeholder="Örn: Tarım Rehberi"
            />
          </FormGroup>
          <FormGroup>
            <Label className="fw-bold">
              İkon Adı <span className="text-danger">*</span>
              <small className="text-muted fw-normal ms-2">(MDI ikon adı — örn: <code>leaf</code>, <code>weather-sunny</code>)</small>
            </Label>
            <div className="d-flex gap-2 align-items-center">
              <Input
                value={formData.IKON_ADI}
                onChange={(e) => f("IKON_ADI", e.target.value)}
                placeholder="leaf"
              />
              {formData.IKON_ADI && (
                <i className={`mdi mdi-${formData.IKON_ADI} font-size-24 text-primary`} title="Önizleme" />
              )}
            </div>
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Renk</Label>
                <div className="d-flex gap-2 align-items-center">
                  <Input
                    type="color"
                    value={formData.RENK}
                    onChange={(e) => f("RENK", e.target.value)}
                    style={{ width: 50, padding: 2, height: 38 }}
                  />
                  <Input
                    value={formData.RENK}
                    onChange={(e) => f("RENK", e.target.value)}
                    placeholder="#4CAF50"
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Sıra No</Label>
                <Input
                  type="number" min="1"
                  value={formData.SIRA}
                  onChange={(e) => f("SIRA", e.target.value)}
                  placeholder="1"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup check className="mt-2">
                <Input
                  type="checkbox"
                  id="isBlog"
                  checked={formData.IS_BLOG}
                  onChange={(e) => f("IS_BLOG", e.target.checked)}
                />
                <Label check for="isBlog" className="fw-bold ms-1">Blog içeriği mi?</Label>
                <p className="text-muted small mb-0">Bu menü blog yazılarını gösterir</p>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup check className="mt-2">
                <Input
                  type="checkbox"
                  id="aktif"
                  checked={formData.AKTIF}
                  onChange={(e) => f("AKTIF", e.target.checked)}
                />
                <Label check for="aktif" className="fw-bold ms-1">Aktif</Label>
                <p className="text-muted small mb-0">Pasif menüler uygulamada görünmez</p>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CiftciBilgiMenuleri;
